import {
    Popover,
    PopoverSurface,
    PopoverTrigger,
    makeStyles,
    tokens,
} from "@fluentui/react-components";
import { CalendarLtr20Regular, Clock20Regular } from "@fluentui/react-icons";
import React, { useEffect, useState } from "react";
import * as Constants from "../../utils/constants";

interface IWeekTableProjectNameCellProps {
    projectName: string;
    companyName: string;
    textColor: string;
    privText: boolean | React.ReactElement<any> | number | string | React.ReactNode[] | null | undefined;
    projectId: string;
    isLocked: boolean;
    onClickDefaultHours?: () => void;
    // Indicates that this project is the configured default project for at least one day in the current week
    isDefaultInWeek?: boolean;
    /** Optional project note/description to show in the hours popover */
    note?: string | null;
    /** Hours logged on this project in the viewed calendar month */
    monthHours?: number;
    /** Hours logged on this project in the viewed calendar year */
    yearHours?: number;
    /** Calendar month label, e.g. "September 2026" */
    monthLabel?: string;
    /** Calendar year label, e.g. "2026" */
    yearLabel?: string;
}

const useStyles = makeStyles({
    surface: {
        minWidth: "min(220px, calc(100vw - 24px))",
        maxWidth: "min(300px, calc(100vw - 24px))",
        boxSizing: "border-box",
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalS,
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        boxShadow: tokens.shadow16,
    },
    header: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        minWidth: 0,
    },
    projectTitle: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase300,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    company: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    divider: {
        height: "1px",
        backgroundColor: tokens.colorNeutralStroke2,
        margin: `${tokens.spacingVerticalXXS} 0`,
    },
    row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: tokens.spacingHorizontalM,
    },
    rowLabel: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalXS,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        minWidth: 0,
    },
    rowValue: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
        color: tokens.colorNeutralForeground1,
    },
    note: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        lineHeight: tokens.lineHeightBase200,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    hint: {
        fontSize: tokens.fontSizeBase100,
        color: tokens.colorNeutralForeground4,
    },
});

const formatHours = (hours: number | undefined): string => {
    const value = typeof hours === "number" && !Number.isNaN(hours) ? hours : 0;
    return `${value.toFixed(1)} h`;
};

export default function WeekTableProjectNameCell(props: IWeekTableProjectNameCellProps) {
    const styles = useStyles();
    const note = typeof props.note === "string" && props.note.trim().length > 0 ? props.note.trim() : null;
    const monthLabel = props.monthLabel || "This month";
    const yearLabel = props.yearLabel || "This year";
    const hasTotals = props.monthHours !== undefined || props.yearHours !== undefined;
    const [isNarrow, setIsNarrow] = useState(
        typeof window !== "undefined" ? window.innerWidth < Constants.MOBILEWIDTH : true,
    );

    useEffect(() => {
        const onResize = () => setIsNarrow(window.innerWidth < Constants.MOBILEWIDTH);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const cellContent = (
        <div
            className={`text-left overflow-hidden text-ellipsis whitespace-nowrap w-full text-xs sm:text-xs outline-none ${
                props.onClickDefaultHours
                    ? "cursor-pointer hover:bg-(--colorNeutralBackground1Hover) rounded-md px-1 py-0.5 -mx-1"
                    : "cursor-help"
            }`}
            onClick={(e) => {
                // Default-hours row click should still work; don't let popover steal it.
                if (props.onClickDefaultHours) {
                    e.stopPropagation();
                    props.onClickDefaultHours();
                }
            }}
            tabIndex={0}
            aria-label={`${props.projectName}, ${formatHours(props.monthHours)} in ${monthLabel}, ${formatHours(props.yearHours)} in ${yearLabel}`}
        >
            <div
                className={`text-sm sm:text-xs font-semibold overflow-hidden text-ellipsis mb-0.5 sm:mb-0.5 flex items-center gap-1.5 sm:gap-1 ${props.textColor}`}
            >
                {props.isLocked && (
                    <span className="text-red-600 dark:text-red-400 text-base sm:text-sm" title="Project entries are locked">
                        🔒
                    </span>
                )}
                <span className="truncate">{props.projectName}</span>
                {props.isDefaultInWeek && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-red-500 text-red-600 dark:text-red-500 bg-red-50 dark:bg-white text-[0.6rem] sm:text-[0.55rem] font-semibold whitespace-nowrap">
                        DEFAULT PROJECT
                    </span>
                )}
            </div>
            <div
                className="text-xs sm:text-xs overflow-hidden text-ellipsis text-(--colorNeutralForeground1) opacity-70"
                title={props.privText ? String(props.privText) : undefined}
            >
                {props.companyName}
            </div>
        </div>
    );

    if (!hasTotals && !note) {
        return cellContent;
    }

    // Mobile: open below the name (cards are full-width). Desktop: open to the right.
    // fallbackPositions + viewport overflow keep the surface on-screen when space is tight.
    const positioning = isNarrow
        ? {
              position: "below" as const,
              align: "start" as const,
              offset: 8,
              fallbackPositions: ["above", "below", "after", "before"] as const,
              overflowBoundary: typeof document !== "undefined" ? document.body : undefined,
          }
        : {
              position: "after" as const,
              align: "top" as const,
              offset: 8,
              fallbackPositions: ["before", "below", "above", "after"] as const,
              overflowBoundary: typeof document !== "undefined" ? document.body : undefined,
          };

    return (
        <Popover
            openOnHover
            mouseLeaveDelay={200}
            positioning={positioning}
            withArrow
            // Keep keyboard focus in the time grid while inspecting project totals
            unstable_disableAutoFocus
            closeOnScroll
        >
            <PopoverTrigger disableButtonEnhancement>{cellContent}</PopoverTrigger>
            <PopoverSurface className={styles.surface} tabIndex={-1}>
                <div className={styles.header}>
                    <div className={`${styles.projectTitle} ${props.textColor}`}>{props.projectName}</div>
                    <div className={styles.company}>{props.companyName}</div>
                </div>

                {hasTotals && (
                    <>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            <div className={styles.rowLabel}>
                                <CalendarLtr20Regular />
                                <span className="truncate">{monthLabel}</span>
                            </div>
                            <div className={styles.rowValue}>{formatHours(props.monthHours)}</div>
                        </div>
                        <div className={styles.row}>
                            <div className={styles.rowLabel}>
                                <Clock20Regular />
                                <span className="truncate">{yearLabel}</span>
                            </div>
                            <div className={styles.rowValue}>{formatHours(props.yearHours)}</div>
                        </div>
                    </>
                )}

                {note && (
                    <>
                        <div className={styles.divider} />
                        <div className={styles.note}>{note}</div>
                    </>
                )}

                <div className={styles.hint}>Your logged hours</div>
            </PopoverSurface>
        </Popover>
    );
}
